import React, { useState, useEffect, useMemo } from 'react';
import { 
  Target, Users, Activity, TrendingUp, TrendingDown, AlertTriangle,
  ChevronRight, X, CheckCircle2, AlertCircle, Zap, Shield, Brain,
  BarChart3, Layers, Sparkles, ChevronDown, Flame, UserX, Info
} from 'lucide-react';
import { COGNITIVE_LANES, COGNITIVE_LEVELS, teamMembers, tasks } from '../mock';
import { DESIGN } from './DesignSystem';

// ================================================
// COGNITIVE MAP COLORS
// ================================================
const MAP_COLORS = {
  background: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceDark: '#0D1117',
  border: 'rgba(0, 0, 0, 0.08)',
  text: '#1E293B',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  primary: '#00C9A7',
  teal: '#14B8A6',
  amber: '#F59E0B'
};

// ================================================
// STATUS CONFIGURATIONS
// ================================================
const STATUS_CONFIG = {
  strong: { label: 'Strong', color: MAP_COLORS.success, icon: CheckCircle2, bg: 'rgba(16, 185, 129, 0.1)' },
  healthy: { label: 'Healthy', color: MAP_COLORS.teal, icon: CheckCircle2, bg: 'rgba(20, 184, 166, 0.1)' },
  thin: { label: 'Thin', color: MAP_COLORS.warning, icon: AlertTriangle, bg: 'rgba(245, 158, 11, 0.1)' },
  overloaded: { label: 'Overloaded', color: MAP_COLORS.warning, icon: Flame, bg: 'rgba(245, 158, 11, 0.1)' },
  atRisk: { label: 'At Risk', color: MAP_COLORS.error, icon: AlertCircle, bg: 'rgba(239, 68, 68, 0.1)' },
  vacant: { label: 'Vacant', color: MAP_COLORS.error, icon: UserX, bg: 'rgba(239, 68, 68, 0.1)' }
};

// ================================================
// MOCK COLLABORATION DATA
// ================================================
const MOCK_COLLABS = [
  { id: 'c1', lane1: 'strategy', lane2: 'operations', name: 'Q2 Operational Planning', activeTasks: 5 },
  { id: 'c2', lane1: 'creative', lane2: 'marketing', name: 'Brand Campaign Q2', activeTasks: 3 },
  { id: 'c3', lane1: 'revenue', lane2: 'relationships', name: 'Enterprise Sales Initiative', activeTasks: 4 },
  { id: 'c4', lane1: 'vision', lane2: 'strategy', name: 'Annual Vision Review', activeTasks: 2 },
  { id: 'c5', lane1: 'operations', lane2: 'execution', name: 'Process Optimization', activeTasks: 6 },
  { id: 'c6', lane1: 'finance', lane2: 'operations', name: 'Budget Review Q2', activeTasks: 2 },
  { id: 'c7', lane1: 'marketing', lane2: 'revenue', name: 'Growth Marketing Sprint', activeTasks: 4 }
];

// ================================================
// DEPARTMENT GROUPINGS
// ================================================
const DEPARTMENT_GROUPS = {
  'Marketing & Creative': ['marketing', 'creative', 'relationships'],
  'Operations & Execution': ['operations', 'execution', 'finance'],
  'Strategy & Leadership': ['vision', 'strategy', 'culture'],
  'Revenue': ['revenue']
};

const REGION_GROUPS = {
  'East Region': ['operations', 'execution', 'marketing'],
  'West Region': ['creative', 'strategy', 'finance'],
  'National': ['vision', 'revenue', 'relationships'],
  'Soul Food': ['culture']
};

const SOUL_FOOD_LANES = ['creative', 'marketing', 'relationships', 'culture'];

// ================================================
// SPARKLINE COMPONENT
// ================================================
const Sparkline = ({ data, color, width = 80, height = 20 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={width} cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2} r="2.5" fill={color} />
    </svg>
  );
};

// ================================================
// LANE HEALTH CARD COMPONENT
// ================================================
const LaneHealthCard = ({ laneData, isDimmed, isHighlighted, onClick }) => {
  const { lane, memberCount, avgLoad, onTimeRate, healthScore, status, aiStatus, members, burnoutRiskCount } = laneData;
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;
  
  return (
    <div 
      onClick={onClick}
      className={`relative p-5 rounded-2xl cursor-pointer transition-all duration-300 ${
        isDimmed ? 'opacity-20' : ''
      } ${isHighlighted ? 'ring-2 ring-offset-2' : ''}`}
      style={{ 
        backgroundColor: isDimmed ? 'rgba(0,0,0,0.03)' : MAP_COLORS.surface,
        borderLeft: `4px solid ${lane.color}`,
        boxShadow: isDimmed ? 'none' : isHighlighted 
          ? `0 8px 30px ${lane.color}30, 0 0 0 2px ${lane.color}40` 
          : DESIGN.shadows.md,
        ringColor: isHighlighted ? lane.color : 'transparent'
      }}
      data-testid={`lane-card-${lane.id}`}
    >
      {/* Background wash */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-5"
        style={{ backgroundColor: lane.color }}
      />
      
      {/* Status Badge */}
      <div 
        className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
        style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        {statusConfig.label}
      </div>
      
      {/* Top Section - Lane Identity */}
      <div className="flex items-start gap-3 mb-4">
        <div 
          className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
          style={{ backgroundColor: lane.color, boxShadow: `0 0 8px ${lane.color}50` }}
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold" style={{ color: MAP_COLORS.text }}>
            {lane.name}
          </h3>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-black" style={{ color: MAP_COLORS.text }}>
              {memberCount}
            </span>
            <span className="text-sm" style={{ color: MAP_COLORS.textSecondary }}>
              members
            </span>
          </div>
        </div>
      </div>
      
      {/* Middle Section - Health Metrics */}
      <div className="space-y-3 mb-4">
        {/* On-Time Rate Bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium" style={{ color: MAP_COLORS.textSecondary }}>On-Time Rate</span>
            <span className="text-xs font-bold" style={{ color: lane.color }}>{onTimeRate}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${onTimeRate}%`, backgroundColor: lane.color }}
            />
          </div>
        </div>
        
        {/* Current Load Bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium" style={{ color: MAP_COLORS.textSecondary }}>Current Load</span>
            <span className="text-xs font-bold" style={{ 
              color: avgLoad >= 85 ? MAP_COLORS.error : avgLoad >= 70 ? MAP_COLORS.warning : MAP_COLORS.success 
            }}>{avgLoad}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${avgLoad}%`, 
                backgroundColor: avgLoad >= 85 ? MAP_COLORS.error : avgLoad >= 70 ? MAP_COLORS.warning : MAP_COLORS.success 
              }}
            />
          </div>
        </div>
        
        {/* Lane Health Score */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: MAP_COLORS.textSecondary }}>
            Lane Health
          </span>
          <span 
            className="text-2xl font-black"
            style={{ 
              color: healthScore >= 80 ? MAP_COLORS.success : healthScore >= 60 ? MAP_COLORS.warning : MAP_COLORS.error 
            }}
          >
            {healthScore}
          </span>
        </div>
      </div>
      
      {/* Bottom Section - AI Status */}
      <div 
        className="p-3 rounded-xl text-sm leading-relaxed"
        style={{ backgroundColor: 'rgba(0,0,0,0.03)', color: MAP_COLORS.textSecondary }}
      >
        {aiStatus}
      </div>
      
      {/* Click indicator */}
      <div className="absolute bottom-4 right-4">
        <ChevronRight className="w-4 h-4" style={{ color: MAP_COLORS.textMuted }} />
      </div>
    </div>
  );
};

// ================================================
// CROSS-LANE COLLABORATIONS SECTION
// ================================================
const CrossLaneCollaborations = ({ collabs }) => (
  <div 
    className="p-6 rounded-2xl mt-6"
    style={{ backgroundColor: MAP_COLORS.surface, boxShadow: DESIGN.shadows.md }}
    data-testid="cross-lane-collaborations"
  >
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${MAP_COLORS.primary}15` }}>
        <Layers className="w-5 h-5" style={{ color: MAP_COLORS.primary }} />
      </div>
      <div>
        <h3 className="text-base font-bold" style={{ color: MAP_COLORS.text }}>Active Cross-Lane Collaborations</h3>
        <p className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>{collabs.length} active projects connecting cognitive lanes</p>
      </div>
    </div>
    
    <div className="space-y-3">
      {collabs.map(collab => {
        const lane1 = COGNITIVE_LANES.find(l => l.id === collab.lane1);
        const lane2 = COGNITIVE_LANES.find(l => l.id === collab.lane2);
        
        return (
          <div key={collab.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
            {/* Lane color dots with connecting line */}
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lane1?.color }} />
              <div className="w-6 h-0.5 rounded" style={{ 
                background: `linear-gradient(90deg, ${lane1?.color} 0%, ${lane2?.color} 100%)`
              }} />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lane2?.color }} />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: MAP_COLORS.text }}>
                {lane1?.name} + {lane2?.name}
              </p>
              <p className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>
                {collab.name} — {collab.activeTasks} active tasks
              </p>
            </div>
            
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: MAP_COLORS.textMuted }} />
          </div>
        );
      })}
    </div>
  </div>
);

// ================================================
// COGNITIVE COVERAGE ANALYSIS PANEL
// ================================================
const CognitiveAnalysisPanel = ({ laneStats, cognitiveHealthScore }) => {
  const [showExplanation, setShowExplanation] = useState(false);
  
  const strongLanes = laneStats.filter(l => l.status === 'strong' || l.status === 'healthy');
  const thinLanes = laneStats.filter(l => l.status === 'thin');
  const vacantLanes = laneStats.filter(l => l.status === 'vacant');
  const overloadedLanes = laneStats.filter(l => l.status === 'overloaded' || l.status === 'atRisk');
  
  // Generate recommended actions
  const recommendations = useMemo(() => {
    const actions = [];
    
    if (vacantLanes.length > 0) {
      const lane = vacantLanes[0];
      actions.push({
        priority: 'high',
        text: `Hire or designate a primary ${lane.lane.name} lane thinker — current misassignment is creating execution drag.`
      });
    }
    
    if (thinLanes.length > 0) {
      const lane = thinLanes[0];
      const member = lane.members[0];
      actions.push({
        priority: 'medium',
        text: `Cross-train a second ${lane.lane.name} lane member — ${member?.name || 'current member'} shows secondary ${lane.lane.name} lane aptitude.`
      });
    }
    
    if (overloadedLanes.length > 0) {
      const lane = overloadedLanes[0];
      actions.push({
        priority: 'medium',
        text: `Redistribute ${lane.lane.name} workload — ${lane.burnoutRiskCount || 1} team members at elevated burnout risk.`
      });
    }
    
    return actions.slice(0, 3);
  }, [thinLanes, vacantLanes, overloadedLanes]);
  
  // Generate health explanation
  const healthExplanation = useMemo(() => {
    if (cognitiveHealthScore >= 80) {
      return `${cognitiveHealthScore} — Strong organizational cognitive health. Your lanes are well-staffed and aligned. ${
        thinLanes.length > 0 ? `${thinLanes.length} thin lane${thinLanes.length > 1 ? 's' : ''} present${thinLanes.length > 0 ? ' moderate risk' : ''}. ` : ''
      }${vacantLanes.length > 0 ? `Address ${vacantLanes.map(l => l.lane.name).join(' and ')} coverage to reach optimal health.` : ''}`;
    } else if (cognitiveHealthScore >= 60) {
      return `${cognitiveHealthScore} — Moderate cognitive health. Some lanes need attention. ${thinLanes.length + vacantLanes.length} coverage gaps identified. Prioritize hiring and cross-training to improve resilience.`;
    }
    return `${cognitiveHealthScore} — Cognitive health needs attention. Multiple lanes are understaffed or overloaded. Immediate action recommended to prevent burnout and execution gaps.`;
  }, [cognitiveHealthScore, thinLanes, vacantLanes]);

  return (
    <div className="space-y-5" data-testid="cognitive-analysis-panel">
      {/* Cognitive Health Score with explanation */}
      <div 
        className="p-5 rounded-2xl"
        style={{ backgroundColor: MAP_COLORS.surface, boxShadow: DESIGN.shadows.md }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: MAP_COLORS.textSecondary }}>
              Cognitive Health Score
            </p>
            <p 
              className="text-4xl font-black"
              style={{ 
                color: cognitiveHealthScore >= 80 ? MAP_COLORS.success : 
                       cognitiveHealthScore >= 60 ? MAP_COLORS.warning : MAP_COLORS.error
              }}
            >
              {cognitiveHealthScore}
            </p>
          </div>
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ 
              background: `conic-gradient(${cognitiveHealthScore >= 80 ? MAP_COLORS.success : cognitiveHealthScore >= 60 ? MAP_COLORS.warning : MAP_COLORS.error} ${cognitiveHealthScore * 3.6}deg, #E5E7EB 0deg)`
            }}
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
              <Brain className="w-6 h-6" style={{ color: MAP_COLORS.primary }} />
            </div>
          </div>
        </div>
        
        {/* What this means */}
        <button 
          onClick={() => setShowExplanation(!showExplanation)}
          className="flex items-center gap-2 text-xs font-medium w-full text-left"
          style={{ color: MAP_COLORS.primary }}
        >
          <Info className="w-3.5 h-3.5" />
          What this means
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExplanation ? 'rotate-180' : ''}`} />
        </button>
        
        {showExplanation && (
          <p className="mt-3 text-sm leading-relaxed p-3 rounded-xl" style={{ 
            backgroundColor: 'rgba(0,0,0,0.03)', 
            color: MAP_COLORS.textSecondary 
          }}>
            {healthExplanation}
          </p>
        )}
      </div>
      
      {/* Coverage Categories */}
      <div 
        className="p-5 rounded-2xl"
        style={{ backgroundColor: MAP_COLORS.surface, boxShadow: DESIGN.shadows.md }}
      >
        <h3 className="text-base font-bold mb-4" style={{ color: MAP_COLORS.text }}>
          Cognitive Coverage Analysis
        </h3>
        
        <div className="space-y-4">
          {/* Strong Lanes */}
          {strongLanes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: MAP_COLORS.success }} />
                <span className="text-sm font-semibold" style={{ color: MAP_COLORS.success }}>
                  Strong Lanes
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {strongLanes.map(l => (
                  <span key={l.lane.id} className="px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: `${l.lane.color}15`, color: l.lane.color }}>
                    {l.lane.name}
                  </span>
                ))}
              </div>
              <p className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>
                Well-staffed with healthy load distribution and high on-time rates.
              </p>
            </div>
          )}
          
          {/* Thin Lanes */}
          {thinLanes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" style={{ color: MAP_COLORS.warning }} />
                <span className="text-sm font-semibold" style={{ color: MAP_COLORS.warning }}>
                  Thin Lanes
                </span>
              </div>
              {thinLanes.map(l => (
                <div key={l.lane.id} className="p-3 rounded-xl mb-2" style={{ backgroundColor: STATUS_CONFIG.thin.bg }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: l.lane.color }} />
                    <span className="text-sm font-medium" style={{ color: MAP_COLORS.text }}>{l.lane.name}</span>
                  </div>
                  <p className="text-xs" style={{ color: MAP_COLORS.warning }}>
                    {l.memberCount} member{l.memberCount > 1 ? 's' : ''}{l.members[0] ? `, ${l.members[0].name}` : ''}. Single point of failure.
                  </p>
                </div>
              ))}
            </div>
          )}
          
          {/* Vacant Lanes */}
          {vacantLanes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <UserX className="w-4 h-4" style={{ color: MAP_COLORS.error }} />
                <span className="text-sm font-semibold" style={{ color: MAP_COLORS.error }}>
                  Vacant Lanes
                </span>
              </div>
              {vacantLanes.map(l => (
                <div key={l.lane.id} className="p-3 rounded-xl mb-2" style={{ backgroundColor: STATUS_CONFIG.vacant.bg }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: l.lane.color }} />
                    <span className="text-sm font-medium" style={{ color: MAP_COLORS.text }}>{l.lane.name}</span>
                  </div>
                  <p className="text-xs" style={{ color: MAP_COLORS.error }}>
                    No dedicated thinker. Work currently handled by misaligned team members.
                  </p>
                </div>
              ))}
            </div>
          )}
          
          {/* Overloaded Lanes */}
          {overloadedLanes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4" style={{ color: MAP_COLORS.warning }} />
                <span className="text-sm font-semibold" style={{ color: MAP_COLORS.warning }}>
                  Overloaded Lanes
                </span>
              </div>
              {overloadedLanes.map(l => (
                <div key={l.lane.id} className="p-3 rounded-xl mb-2" style={{ backgroundColor: STATUS_CONFIG.overloaded.bg }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: l.lane.color }} />
                      <span className="text-sm font-medium" style={{ color: MAP_COLORS.text }}>{l.lane.name}</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: MAP_COLORS.warning }}>{l.avgLoad}% load</span>
                  </div>
                  <p className="text-xs" style={{ color: MAP_COLORS.warning }}>
                    {l.burnoutRiskCount || 0} member{(l.burnoutRiskCount || 0) !== 1 ? 's' : ''} at burnout risk.
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Recommended Actions */}
      {recommendations.length > 0 && (
        <div 
          className="p-5 rounded-2xl"
          style={{ backgroundColor: MAP_COLORS.surface, boxShadow: DESIGN.shadows.md }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: MAP_COLORS.text }}>
            Recommended Actions
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-white"
                  style={{ backgroundColor: rec.priority === 'high' ? MAP_COLORS.error : MAP_COLORS.warning }}
                >
                  {idx + 1}
                </div>
                <p className="text-sm" style={{ color: MAP_COLORS.textSecondary }}>
                  {rec.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// LANE DRAWER COMPONENT
// ================================================
const LaneDrawer = ({ lane, members, activeTasks, collabs, healthScore, onClose }) => {
  if (!lane) return null;
  
  const avgLoad = members.length > 0 
    ? Math.round(members.reduce((acc, m) => acc + ((m.current_load || 3) / m.max_task_load), 0) / members.length * 100)
    : 0;
  const avgOnTime = 75 + Math.random() * 20;
  const trendData = [65, 70, 68, 75, 72, 78, 80, avgOnTime];
  
  return (
    <div 
      className="fixed right-0 top-0 h-full w-[420px] z-50 overflow-y-auto"
      style={{ backgroundColor: '#FFFFFF', borderLeft: `1px solid ${MAP_COLORS.border}`, boxShadow: '-10px 0 40px rgba(0,0,0,0.1)' }}
      data-testid="lane-drawer"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 p-5" style={{ background: `linear-gradient(180deg, ${lane.color}20 0%, #FFFFFF 100%)`, borderBottom: `1px solid ${MAP_COLORS.border}` }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: lane.color, boxShadow: `0 0 20px ${lane.color}40` }}>
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ color: MAP_COLORS.text }}>{lane.name}</h2>
              <p className="text-sm" style={{ color: MAP_COLORS.textSecondary }}>{members.length} team members</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" style={{ color: MAP_COLORS.textMuted }} />
          </button>
        </div>
        
        {/* Lane Health Score */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: MAP_COLORS.textSecondary }}>Lane Health Score</span>
            <span className="text-2xl font-black" style={{ color: healthScore >= 70 ? MAP_COLORS.success : healthScore >= 50 ? MAP_COLORS.warning : MAP_COLORS.error }}>
              {healthScore}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${healthScore}%`, backgroundColor: healthScore >= 70 ? MAP_COLORS.success : healthScore >= 50 ? MAP_COLORS.warning : MAP_COLORS.error }} />
          </div>
        </div>
      </div>
      
      <div className="p-5 space-y-6">
        {/* On-Time Trend */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: MAP_COLORS.text }}>On-Time Rate (8 weeks)</h3>
            <span className="text-sm font-bold" style={{ color: MAP_COLORS.success }}>{Math.round(avgOnTime)}%</span>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
            <Sparkline data={trendData} color={lane.color} width={350} height={40} />
          </div>
        </div>
        
        {/* Team Members */}
        <div>
          <h3 className="text-sm font-semibold mb-3" style={{ color: MAP_COLORS.text }}>Team Members ({members.length})</h3>
          <div className="space-y-2">
            {members.slice(0, 6).map(member => {
              const load = (member.current_load || 3) / member.max_task_load;
              const level = member.lane_proficiency[lane.id] || 'L2';
              return (
                <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: lane.color }}>
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: MAP_COLORS.text }}>{member.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${DESIGN.levels[level]?.color || '#666'}20`, color: DESIGN.levels[level]?.color || '#666' }}>{level}</span>
                      <span className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>{member.title}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="w-16 h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${load * 100}%`, backgroundColor: load > 0.8 ? MAP_COLORS.error : load > 0.6 ? MAP_COLORS.warning : MAP_COLORS.success }} />
                    </div>
                    <span className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>{Math.round(load * 100)}% load</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Active Tasks */}
        <div>
          <h3 className="text-sm font-semibold mb-3" style={{ color: MAP_COLORS.text }}>Active Tasks ({activeTasks.length})</h3>
          <div className="space-y-2">
            {activeTasks.slice(0, 4).map(task => (
              <div key={task.id} className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                <p className="text-sm font-medium truncate" style={{ color: MAP_COLORS.text }}>{task.title}</p>
                <p className="text-xs mt-1" style={{ color: MAP_COLORS.textSecondary }}>{task.project_name || 'General'}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Active Collabs */}
        {collabs.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: MAP_COLORS.text }}>Cross-Lane Collaborations ({collabs.length})</h3>
            <div className="space-y-2">
              {collabs.map(collab => {
                const otherLaneId = collab.lane1 === lane.id ? collab.lane2 : collab.lane1;
                const otherLane = COGNITIVE_LANES.find(l => l.id === otherLaneId);
                return (
                  <div key={collab.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: otherLane?.color }}>
                      <Layers className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: MAP_COLORS.text }}>{otherLane?.name}</p>
                      <p className="text-xs" style={{ color: MAP_COLORS.textSecondary }}>{collab.activeTasks} active tasks</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// MAIN COGNITIVE MAP COMPONENT
// ================================================
const CognitiveMap = () => {
  const [selectedLane, setSelectedLane] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // Generate AI status line for each lane
  const generateAIStatus = (laneData) => {
    const { memberCount, avgLoad, onTimeRate, burnoutRiskCount, members } = laneData;
    
    if (memberCount === 0) {
      return "Vacant — no team members assigned. Work in this lane is unassigned or misaligned.";
    }
    if (memberCount === 1) {
      return `At risk — only 1 member, single point of failure. Consider cross-training.`;
    }
    if (avgLoad >= 85) {
      return `Overloaded — average capacity at ${avgLoad}%, burnout risk elevated for ${burnoutRiskCount || 1} member${burnoutRiskCount !== 1 ? 's' : ''}.`;
    }
    if (onTimeRate >= 85 && avgLoad < 75) {
      return "Strong — executing above target with healthy load distribution.";
    }
    if (onTimeRate >= 75) {
      return "Healthy — on-track execution with manageable workload levels.";
    }
    return `Needs attention — on-time rate at ${onTimeRate}%. Review workload and priorities.`;
  };
  
  // Determine lane status
  const determineLaneStatus = (laneData) => {
    const { memberCount, avgLoad, onTimeRate, burnoutRiskCount } = laneData;
    
    if (memberCount === 0) return 'vacant';
    if (memberCount === 1) return 'thin';
    if (avgLoad >= 85 || burnoutRiskCount >= 2) return 'overloaded';
    if (avgLoad >= 75) return 'atRisk';
    if (onTimeRate >= 85 && avgLoad < 70) return 'strong';
    return 'healthy';
  };
  
  // Calculate lane statistics
  const laneStats = useMemo(() => {
    return COGNITIVE_LANES.map(lane => {
      const members = teamMembers.filter(tm => tm.lane_proficiency && tm.lane_proficiency[lane.id]);
      const laneTasks = tasks.filter(t => t.primary_lane === lane.id);
      const collabs = MOCK_COLLABS.filter(c => c.lane1 === lane.id || c.lane2 === lane.id);
      
      const avgLoad = members.length > 0 
        ? Math.round(members.reduce((acc, m) => acc + ((m.current_load || 3) / m.max_task_load), 0) / members.length * 100)
        : 0;
      
      const burnoutRiskCount = members.filter(m => ((m.current_load || 3) / m.max_task_load) > 0.8).length;
      const onTimeRate = Math.round(70 + Math.random() * 25);
      const healthScore = members.length === 0 ? 0 : 
        Math.round(100 - (avgLoad * 0.3) - (burnoutRiskCount * 15) + (onTimeRate * 0.2) - (members.length <= 1 ? 20 : 0));
      
      const laneData = {
        lane,
        members,
        memberCount: members.length,
        tasks: laneTasks,
        collabs,
        avgLoad,
        onTimeRate,
        burnoutRiskCount,
        healthScore: Math.max(0, Math.min(100, healthScore))
      };
      
      return {
        ...laneData,
        status: determineLaneStatus(laneData),
        aiStatus: generateAIStatus(laneData)
      };
    });
  }, []);
  
  // Calculate overall cognitive health score
  const cognitiveHealthScore = useMemo(() => {
    const activeLanes = laneStats.filter(l => l.memberCount > 0);
    if (activeLanes.length === 0) return 0;
    
    const avgHealth = activeLanes.reduce((acc, l) => acc + l.healthScore, 0) / activeLanes.length;
    const coveragePenalty = (laneStats.length - activeLanes.length) * 5;
    const thinPenalty = laneStats.filter(l => l.memberCount === 1).length * 3;
    
    return Math.max(0, Math.round(avgHealth - coveragePenalty - thinPenalty));
  }, [laneStats]);
  
  // Filter logic
  const getFilteredStats = () => {
    if (filter === 'all') return laneStats;
    if (filter === 'soulfood') return laneStats; // Will dim non-soulfood
    return laneStats;
  };
  
  const isDimmed = (laneId) => {
    if (filter === 'soulfood') {
      return !SOUL_FOOD_LANES.includes(laneId);
    }
    return false;
  };
  
  const isHighlighted = (laneId) => {
    if (filter === 'soulfood') {
      return SOUL_FOOD_LANES.includes(laneId);
    }
    return false;
  };
  
  // Get grouped data for department/region filters
  const getGroupedData = () => {
    if (filter === 'department') return DEPARTMENT_GROUPS;
    if (filter === 'region') return REGION_GROUPS;
    return null;
  };
  
  const groupedData = getGroupedData();
  const selectedLaneData = selectedLane ? laneStats.find(l => l.lane.id === selectedLane) : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: MAP_COLORS.background }} data-testid="cognitive-map">
      {/* Top Controls */}
      <div className="flex items-center justify-between p-5 bg-white border-b" style={{ borderColor: MAP_COLORS.border }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${MAP_COLORS.primary}15` }}>
            <Brain className="w-5 h-5" style={{ color: MAP_COLORS.primary }} />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: MAP_COLORS.text }}>Organizational Cognitive Map</h1>
            <p className="text-sm" style={{ color: MAP_COLORS.textSecondary }}>Lane health at a glance</p>
          </div>
        </div>
        
        {/* Filter Buttons */}
        <div className="flex items-center gap-2">
          {['all', 'department', 'region', 'soulfood'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: filter === f ? MAP_COLORS.primary : 'transparent',
                color: filter === f ? 'white' : MAP_COLORS.textSecondary,
                border: `1px solid ${filter === f ? MAP_COLORS.primary : MAP_COLORS.border}`
              }}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All Lanes' : f === 'department' ? 'By Department' : f === 'region' ? 'By Region' : 'Soul Food Only'}
            </button>
          ))}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex p-5 gap-6">
        {/* Lane Health Grid */}
        <div className="flex-1">
          {groupedData ? (
            // Grouped view
            <div className="space-y-8">
              {Object.entries(groupedData).map(([group, laneIds]) => (
                <div key={group}>
                  <h2 className="text-lg font-bold mb-4 pb-2 border-b" style={{ color: MAP_COLORS.text, borderColor: MAP_COLORS.border }}>
                    {group}
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    {laneStats
                      .filter(l => laneIds.includes(l.lane.id))
                      .map(laneData => (
                        <LaneHealthCard
                          key={laneData.lane.id}
                          laneData={laneData}
                          isDimmed={isDimmed(laneData.lane.id)}
                          isHighlighted={isHighlighted(laneData.lane.id)}
                          onClick={() => setSelectedLane(laneData.lane.id)}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Standard grid view
            <div className="grid grid-cols-2 gap-4" data-testid="lane-grid">
              {getFilteredStats().map(laneData => (
                <LaneHealthCard
                  key={laneData.lane.id}
                  laneData={laneData}
                  isDimmed={isDimmed(laneData.lane.id)}
                  isHighlighted={isHighlighted(laneData.lane.id)}
                  onClick={() => setSelectedLane(laneData.lane.id)}
                />
              ))}
            </div>
          )}
          
          {/* Cross-Lane Collaborations */}
          <CrossLaneCollaborations collabs={MOCK_COLLABS} />
        </div>
        
        {/* Cognitive Coverage Analysis Panel */}
        <div className="w-96 flex-shrink-0">
          <CognitiveAnalysisPanel 
            laneStats={laneStats} 
            cognitiveHealthScore={cognitiveHealthScore}
          />
        </div>
      </div>
      
      {/* Lane Drawer */}
      {selectedLaneData && (
        <LaneDrawer
          lane={selectedLaneData.lane}
          members={selectedLaneData.members}
          activeTasks={selectedLaneData.tasks}
          collabs={selectedLaneData.collabs}
          healthScore={selectedLaneData.healthScore}
          onClose={() => setSelectedLane(null)}
        />
      )}
    </div>
  );
};

export default CognitiveMap;
